import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiLoadingSpinner,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useQuestionnaireSave } from "./index";

/**
 * Compact save-status indicator shown next to the questionnaire save button.
 * Renders nothing while idle; otherwise shows saving / unsaved / saved / error,
 * the last offering a click-to-retry badge.
 */
export const QuestionnaireSaveIndicator = (): JSX.Element | null => {
  const { translate } = useAnketaI18n();
  const { status, errorMessage, retry } = useQuestionnaireSave();

  if (status === "idle") {
    return null;
  }

  if (status === "saving") {
    return (
      <EuiFlexItem grow={false}>
        <EuiFlexGroup
          alignItems="center"
          gutterSize="xs"
          responsive={false}
          aria-live="polite"
        >
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="s" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              {translate("anketa.questionnaire.save.status.saving", {
                defaultMessage: "Saving…",
              })}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    );
  }

  if (status === "unsaved") {
    return (
      <EuiFlexItem grow={false}>
        <EuiHealth color="warning" textSize="xs">
          {translate("anketa.questionnaire.save.status.unsaved", {
            defaultMessage: "Unsaved changes",
          })}
        </EuiHealth>
      </EuiFlexItem>
    );
  }

  if (status === "saved") {
    return (
      <EuiFlexItem grow={false}>
        <EuiHealth color="success" textSize="xs">
          {translate("anketa.questionnaire.save.status.saved", {
            defaultMessage: "Saved",
          })}
        </EuiHealth>
      </EuiFlexItem>
    );
  }

  // status === "error"
  return (
    <EuiFlexItem grow={false}>
      <EuiToolTip
        content={
          errorMessage ??
          translate("anketa.questionnaire.save.status.error", {
            defaultMessage: "Could not save your changes.",
          })
        }
      >
        <EuiBadge
          color="danger"
          iconType="refresh"
          iconSide="left"
          onClick={() => {
            void retry();
          }}
          onClickAriaLabel={translate(
            "anketa.questionnaire.save.status.retry",
            { defaultMessage: "Retry save" },
          )}
        >
          {translate("anketa.questionnaire.save.status.error.short", {
            defaultMessage: "Save failed - retry",
          })}
        </EuiBadge>
      </EuiToolTip>
    </EuiFlexItem>
  );
};
